import React, { Component } from 'react';
import { MENU_CATEGORIES } from '../utils/eventIncluded';

// Los mismos topes que valida el backend al guardar (spec-eventos-consumo-incluido FR-006). Se
// repiten acá sólo para avisar antes de mandar: el que decide es el server, que rechaza con 7607.
export const LIMITS = {
    label: 60,       // caracteres de la etiqueta
    quantity: 99,
    everyN: 99,
    perType: 12,     // renglones por tipo de entrada
    codes: 40,       // ítems de un renglón por ítems
};

const KIND_OPTIONS = [
    { value: 'category', label: 'Categoría del menú' },
    { value: 'products', label: 'Ítems del menú' },
    { value: 'free', label: 'Sólo texto' },
];

const categoryOf = (item) => (MENU_CATEGORIES.some((c) => c.key === item.category) ? item.category : 'other');

// El menú llega entero (también lo que está oculto en el POS). Para elegir se ofrece sólo lo
// visible: un ítem oculto no se puede vender, así que no habría cómo entregarlo.
const isVisible = (item) => !(item.display === 0 || item.display === false);

// Un renglón en blanco: "1 por persona", contra una categoría (lo más común).
export const emptyRow = () => ({ label: '', quantity: '1', everyNPeople: '1', matchKind: 'category', matchValue: [] });

// Cómo se nombra un ítem que ya no está en el menú. NUNCA por su código: un número de trece
// cifras no le dice a nadie qué ítem es.
export const GONE_ITEM = 'Un ítem que ya no está en el menú';
export const goneItemText = (name) => (name ? `${name} — ya no está en el menú` : GONE_ITEM);

// Lo que se muestra de un código guardado mientras no se sabe si sigue en el menú (el menú está
// cargando o no cargó) y el servidor no mandó su nombre (un backend anterior a `items`).
const SAVED_ITEM = 'Un ítem guardado';

// El nombre de cada código de un renglón por ítems tal como lo manda el detalle del evento
// (`inclusions[].items`: `{ code, name, inMenu }`, uno por código): `{ código: nombre }`. Es lo
// que permite nombrar un ítem que salió del menú, que en el menú ya no está. `null` es que su
// ficha ya no existe. Sin `items` (un backend anterior) devuelve null.
export const itemNamesOf = (items) => {
    if (!Array.isArray(items)) return null;
    const names = {};
    items.forEach((item) => {
        if (!item || item.code == null) return;
        const name = String(item.name == null ? '' : item.name).trim();
        names[Number(item.code)] = name !== '' ? name : null;
    });
    return names;
};

// De lo que manda el backend (`ticketType.inclusions`) a los renglones del formulario. Cantidad y
// "cada cuántas" quedan como texto, que es lo que maneja un <input>. Un renglón por ítems guarda
// además los nombres que trae `items` (`itemNames`), sólo para mostrarlos: no viaja al guardar.
export const rowsFromInclusions = (inclusions) => (inclusions || []).map((inc) => {
    const row = {
        label: inc.label || '',
        quantity: inc.quantity != null ? String(inc.quantity) : '1',
        everyNPeople: inc.everyNPeople != null ? String(inc.everyNPeople) : '1',
        matchKind: inc.matchKind || 'category',
        matchValue: Array.isArray(inc.matchValue) ? inc.matchValue.slice() : [],
    };
    const itemNames = itemNamesOf(inc.items);
    if (itemNames && Object.keys(itemNames).length > 0) row.itemNames = itemNames;
    return row;
});

// Los ítems de un renglón por ítems que no están en `menu` (el menú entero de cafetería: lo que
// no está eliminado, visible o no, que es la misma vara con la que valida el servidor), con el
// nombre que guardó el evento si lo hay: `[{ code, name }]`. Un renglón así el servidor lo
// rechaza (7607) hasta que se los quite.
export const itemsNotInMenu = (row, menu) => {
    if (!row || row.matchKind !== 'products' || !Array.isArray(menu)) return [];
    const codes = new Set(menu.map((item) => Number(item.code)));
    const names = row.itemNames || {};
    return (row.matchValue || [])
        .map((code) => Number(code))
        .filter((code) => !codes.has(code))
        .map((code) => ({ code, name: names[code] || null }));
};

// De los renglones del formulario a lo que espera el backend: la lista completa y en orden. El
// valor es siempre un array, y en "sólo texto" no viaja.
export const inclusionsPayload = (rows) => (rows || []).map((row) => {
    const out = {
        label: String(row.label || '').trim(),
        quantity: Number(row.quantity),
        everyNPeople: Number(row.everyNPeople),
        matchKind: row.matchKind,
    };
    if (row.matchKind === 'products') out.matchValue = (row.matchValue || []).map((v) => Number(v));
    if (row.matchKind === 'category') out.matchValue = (row.matchValue || []).map((v) => String(v));
    return out;
});

const entero = (value, max) => {
    const n = Number(String(value).trim());
    return String(value).trim() !== '' && Number.isInteger(n) && n >= 1 && n <= max;
};

// Lo que el backend va a rechazar, dicho antes de mandar. Devuelve el texto del primer problema
// o null. No reemplaza al server: que un ítem siga existiendo en el menú lo sabe sólo él.
export const inclusionsError = (rows) => {
    const list = rows || [];
    if (list.length > LIMITS.perType) return `un tipo de entrada admite hasta ${LIMITS.perType} renglones.`;
    const seen = new Set();
    for (let i = 0; i < list.length; i++) {
        const row = list[i];
        const label = String(row.label || '').trim();
        const which = label !== '' ? `el renglón ${i + 1} («${label}»)` : `el renglón ${i + 1}`;
        if (label === '') return `${which} no tiene etiqueta.`;
        if (label.length > LIMITS.label) return `la etiqueta de ${which} supera los ${LIMITS.label} caracteres.`;
        if (!entero(row.quantity, LIMITS.quantity)) return `la cantidad de ${which} tiene que ser un entero de 1 a ${LIMITS.quantity}.`;
        if (!entero(row.everyNPeople, LIMITS.everyN)) return `«cada cuántas personas» de ${which} tiene que ser un entero de 1 a ${LIMITS.everyN}.`;
        if (row.matchKind === 'free') continue;
        const values = row.matchValue || [];
        if (row.matchKind === 'category' && values.length === 0) return `${which} no tiene ninguna categoría elegida.`;
        if (row.matchKind === 'products' && values.length === 0) return `${which} no tiene ningún ítem elegido.`;
        if (row.matchKind === 'products' && values.length > LIMITS.codes) return `${which} admite hasta ${LIMITS.codes} ítems.`;
        // Misma clase y mismo conjunto, en cualquier orden, es el mismo renglón (FR-007).
        const key = `${row.matchKind}:${[...new Set(values.map((v) => String(v)))].sort().join(',')}`;
        if (seen.has(key)) return `${which} repite lo mismo que otro renglón de este tipo de entrada.`;
        seen.add(key);
    }
    return null;
};

// Sub-formulario "Qué incluye" de un tipo de entrada. Controlado: `value` son los renglones
// (forma de `emptyRow`) y cada cambio sale entero por `onChange`. `menu` es el catálogo de
// cafetería (null mientras carga), que pide quien lo monta.
//
// `disabled` apaga TODOS los controles (mientras se guarda). `unreadable` es otra cosa: el
// servidor no pudo leer qué incluye este tipo (`inclusions: null`). Ahí no se dibuja ningún
// renglón ni se puede agregar: guardar reemplaza la lista entera, y partir de una que nunca se
// leyó borraría lo que el tipo tiene cargado.
//
// `hideHeading` saca el rótulo «Qué incluye» y su aclaración: es para quien lo monta dentro de
// un bloque que ya lleva ese título (el alta de un evento privado).
//
// `onRetryMenu` (opcional): si el menú no cargó, en vez de «Recargá la página» se ofrece
// «Reintentar». Es para un formulario que todavía no guardó nada y que una recarga vaciaría.
class EventInclusionsEditor extends Component {
    rows = () => this.props.value || [];

    emit = (rows) => this.props.onChange(rows);

    patchRow = (index, patch) => this.emit(this.rows().map((row, i) => (i === index ? { ...row, ...patch } : row)));

    addRow = () => this.emit([...this.rows(), emptyRow()]);

    removeRow = (index) => this.emit(this.rows().filter((_, i) => i !== index));

    moveRow = (index, delta) => {
        const rows = this.rows().slice();
        const target = index + delta;
        if (target < 0 || target >= rows.length) return;
        [rows[index], rows[target]] = [rows[target], rows[index]];
        this.emit(rows);
    }

    // Cambiar la clase vacía el valor: una lista de categorías no sirve como lista de ítems. Un
    // renglón de sólo texto no muestra cantidades, así que vuelven a "1 por persona".
    changeKind = (index, matchKind) => this.patchRow(index, {
        matchKind,
        matchValue: [],
        ...(matchKind === 'free' ? { quantity: '1', everyNPeople: '1' } : {}),
    });

    toggleCategory = (index, key) => {
        const current = (this.rows()[index].matchValue || []).map((v) => String(v));
        const next = current.includes(key) ? current.filter((v) => v !== key) : [...current, key];
        this.patchRow(index, { matchValue: next });
    }

    addItem = (index, code) => {
        if (code === '') return;
        const current = (this.rows()[index].matchValue || []).map((v) => Number(v));
        if (current.includes(Number(code))) return;
        this.patchRow(index, { matchValue: [...current, Number(code)] });
    }

    removeItem = (index, code) => {
        const current = (this.rows()[index].matchValue || []).map((v) => Number(v));
        this.patchRow(index, { matchValue: current.filter((v) => v !== Number(code)) });
    }

    renderCategories(row, index) {
        const chosen = (row.matchValue || []).map((v) => String(v));
        return (
            <div>
                {MENU_CATEGORIES.map((cat) => (
                    <label key={cat.key} className="mr-3 mb-0 font-weight-normal">
                        <input
                            type="checkbox"
                            className="mr-1"
                            checked={chosen.includes(cat.key)}
                            disabled={!!this.props.disabled}
                            onChange={() => this.toggleCategory(index, cat.key)}
                        />
                        {cat.label}
                    </label>
                ))}
            </div>
        );
    }

    renderItems(row, index) {
        const menu = this.props.menu;
        const chosen = (row.matchValue || []).map((v) => Number(v));
        const nameOf = (code) => {
            const item = (menu || []).find((m) => Number(m.code) === code);
            return item ? item.name : null;
        };
        const full = chosen.length >= LIMITS.codes;
        // Con el menú a la vista, un código guardado que no está en él es un ítem que salió del
        // menú: el backend rechaza el renglón (7607) hasta que se lo quite. Si el menú no cargó no
        // se puede saber, y no se marca nada.
        const menuKnown = menu != null && !this.props.menuError;
        const isGone = (code) => menuKnown && nameOf(code) == null;
        // El nombre guardado con el evento (`itemNames`, de `items`) es el que nombra a un ítem
        // que ya no está en el menú. El código no se muestra nunca.
        const saved = row.itemNames || {};
        const labelOf = (code) => {
            if (isGone(code)) return goneItemText(saved[code]);
            if (nameOf(code) != null) return nameOf(code);
            if (saved[code]) return saved[code];
            // Sin menú: `null` en lo guardado es que la ficha ya no existe; sin nada guardado no
            // se sabe cuál es.
            return Object.prototype.hasOwnProperty.call(saved, code) ? GONE_ITEM : SAVED_ITEM;
        };
        return (
            <div>
                <div className="mb-2">
                    {chosen.length === 0 && <small className="text-muted">Todavía no elegiste ningún ítem.</small>}
                    {chosen.map((code) => (
                        <span key={code} className={`badge badge-light border mr-1 mb-1 p-2${isGone(code) ? ' border-danger text-danger event-inclusion-gone' : ''}`}>
                            {labelOf(code)}
                            <button
                                type="button"
                                className="btn btn-link btn-sm p-0 ml-2 text-danger"
                                title="Quitar el ítem"
                                disabled={!!this.props.disabled}
                                onClick={() => this.removeItem(index, code)}
                            >
                                ×
                            </button>
                        </span>
                    ))}
                    {chosen.some(isGone) && (
                        <small className="text-danger d-block">
                            Quitá el ítem que ya no está en el menú: con él adentro, este renglón no se puede volver a guardar.
                        </small>
                    )}
                </div>
                {menu == null ? (
                    <small className="text-muted">Cargando el menú…</small>
                ) : (
                    <select
                        className="form-control form-control-sm"
                        style={{ maxWidth: 320 }}
                        value=""
                        disabled={!!this.props.disabled || full || menu.length === 0}
                        onChange={(e) => this.addItem(index, e.target.value)}
                    >
                        <option value="">
                            {full ? `Hasta ${LIMITS.codes} ítems por renglón` : (menu.length === 0 ? 'No hay ítems para elegir' : 'Sumar un ítem del menú…')}
                        </option>
                        {MENU_CATEGORIES.map((cat) => {
                            // Un ítem con una categoría que no es de las seis va a «Otros», igual
                            // que en el POS: si no, no habría cómo elegirlo.
                            const items = menu.filter((m) => isVisible(m) && categoryOf(m) === cat.key && !chosen.includes(Number(m.code)));
                            if (items.length === 0) return null;
                            return (
                                <optgroup key={cat.key} label={cat.label}>
                                    {items.map((m) => <option key={m.code} value={m.code}>{m.name}</option>)}
                                </optgroup>
                            );
                        })}
                    </select>
                )}
                {this.props.menuError && (this.props.onRetryMenu ? (
                    <small className="text-danger d-block mt-1">
                        No se pudo cargar el menú de cafetería.
                        <button type="button" className="btn btn-link btn-sm p-0 ml-1 align-baseline event-inclusions-retry-menu" onClick={this.props.onRetryMenu}>
                            Reintentar
                        </button>
                    </small>
                ) : (
                    <small className="text-danger d-block mt-1">No se pudo cargar el menú de cafetería. Recargá la página para elegir ítems.</small>
                ))}
            </div>
        );
    }

    renderRow = (row, index) => {
        const rows = this.rows();
        const disabled = !!this.props.disabled;
        return (
            <div key={index} className="border rounded p-2 mb-2 event-inclusion-row">
                {/* En pantalla ancha: etiqueta, clase y los botones en la primera línea; los dos
                    números en la segunda, con lugar para leerlos (en una sola línea «Cantidad»
                    quedaba de 25 px a 1024 de ancho). El orden del DOM no cambia. */}
                <div className="row">
                    <div className="col-md-5">
                        <div className="form-group mb-2">
                            <label>Etiqueta *</label>
                            <input
                                type="text"
                                className="form-control form-control-sm"
                                maxLength={LIMITS.label}
                                placeholder="Pizza, Bebida, Lugar en mesa…"
                                value={row.label}
                                disabled={disabled}
                                onChange={(e) => this.patchRow(index, { label: e.target.value })}
                            />
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="form-group mb-2">
                            <label>Clase</label>
                            <select
                                className="form-control form-control-sm"
                                value={row.matchKind}
                                disabled={disabled}
                                onChange={(e) => this.changeKind(index, e.target.value)}
                            >
                                {KIND_OPTIONS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
                            </select>
                        </div>
                    </div>
                    {row.matchKind !== 'free' && (
                        <>
                            <div className="col-6 col-md-3 order-md-2">
                                <div className="form-group mb-2">
                                    <label className="text-nowrap">Cantidad</label>
                                    <input
                                        type="number"
                                        min="1"
                                        max={LIMITS.quantity}
                                        className="form-control form-control-sm"
                                        value={row.quantity}
                                        disabled={disabled}
                                        onChange={(e) => this.patchRow(index, { quantity: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="col-6 col-md-3 order-md-2">
                                <div className="form-group mb-2">
                                    <label className="text-nowrap">Cada cuántas personas</label>
                                    <input
                                        type="number"
                                        min="1"
                                        max={LIMITS.everyN}
                                        className="form-control form-control-sm"
                                        value={row.everyNPeople}
                                        disabled={disabled}
                                        onChange={(e) => this.patchRow(index, { everyNPeople: e.target.value })}
                                    />
                                </div>
                            </div>
                        </>
                    )}
                    <div className="col-md-3 order-md-1 text-right">
                        <label className="d-block">&nbsp;</label>
                        <div className="btn-group btn-group-sm" role="group" aria-label="Orden del renglón">
                            <button type="button" className="btn btn-white" title="Subir" disabled={disabled || index === 0} onClick={() => this.moveRow(index, -1)}>↑</button>
                            <button type="button" className="btn btn-white" title="Bajar" disabled={disabled || index === rows.length - 1} onClick={() => this.moveRow(index, 1)}>↓</button>
                        </div>
                        <button type="button" className="btn btn-sm btn-outline-danger ml-2" disabled={disabled} onClick={() => this.removeRow(index)}>Quitar</button>
                    </div>
                </div>
                {row.matchKind === 'category' && this.renderCategories(row, index)}
                {row.matchKind === 'products' && this.renderItems(row, index)}
                {row.matchKind === 'free' && (
                    <small className="text-muted">No se cuenta contra ningún pedido: sólo se muestra.</small>
                )}
            </div>
        );
    }

    render() {
        const rows = this.rows();
        const full = rows.length >= LIMITS.perType;
        if (this.props.unreadable) {
            return (
                <div className="form-group event-inclusions-editor">
                    <label className="mb-0">Qué incluye</label>
                    <p className="text-danger mb-2 event-inclusions-unreadable">
                        No se pudo leer qué incluye esta entrada. Recargá la página antes de editarla.
                    </p>
                    <button type="button" className="btn btn-sm btn-white" disabled>Agregar renglón</button>
                </div>
            );
        }
        return (
            <div className="form-group event-inclusions-editor">
                {!this.props.hideHeading && (
                    <>
                        <label className="mb-0">Qué incluye</label>
                        <small className="text-muted d-block mb-2">
                            Lo que la entrada trae sin cargo. Es interno: no se publica en la web ni en la tele.
                        </small>
                    </>
                )}
                {rows.map(this.renderRow)}
                <button type="button" className="btn btn-sm btn-white" disabled={!!this.props.disabled || full} onClick={this.addRow}>
                    Agregar renglón
                </button>
                {full && <small className="text-muted ml-2">Hasta {LIMITS.perType} renglones por tipo de entrada.</small>}
            </div>
        );
    }
}

export default EventInclusionsEditor;
